/**
 * Guard Evaluator
 * Unified route guard evaluation system with caching, debouncing, and security best practices
 * 
 * Security Features:
 * - Fail-closed for admin routes (deny by default)
 * - Security event logging
 * - Defense in depth (multiple validation layers)
 * - Performance optimized with intelligent caching
 */

import { usePermissionStore } from '../stores/permissionStore'
import { useRbacStore } from '../stores/rbacStore'
import { isPermissionKey } from '../../../types/rbac/catalogue'
import { resolvePermission } from './rbacPermissions'
import { getRouteMetadata, isPublicRoute, getRedirectTarget, PERMISSIONS } from '../config/routeMetadata'
import { isAdminRoute, normalizeRoutePath, logSecurityEvent, SecurityEventType } from './routeUtils'
import Parse from 'parse'

export interface GuardResult {
  allowed: boolean
  reason?: string
  redirectTo?: string
  loading?: boolean
  securityLevel?: 'public' | 'protected' | 'admin'
}

// Cache for guard results with metadata
interface CacheEntry {
  result: GuardResult
  timestamp: number
  path: string
}

const guardCache = new Map<string, CacheEntry>()
const CACHE_DURATION = 1000 // 1 second cache
const DEBOUNCE_DELAY = 100 // 100ms debounce

// Security: Admin routes should have shorter cache duration
const ADMIN_CACHE_DURATION = 500 // 500ms for admin routes

// Debounce timer
let debounceTimer: NodeJS.Timeout | null = null
let pendingPaths: Set<string> = new Set()

/**
 * Get cache duration based on route type
 */
function getCacheDuration(path: string): number {
  return isAdminRoute(path) ? ADMIN_CACHE_DURATION : CACHE_DURATION
}

/**
 * Check if user has a specific permission
 */
function hasPermission(permission: string, state: ReturnType<typeof usePermissionStore.getState>): boolean {
  // Handle negation permissions (e.g., '!company_access')
  if (permission.startsWith('!')) {
    const positivePermission = permission.slice(1)
    return !hasPermission(positivePermission, state)
  }
  
  switch (permission) {
    case PERMISSIONS.GLOBAL_ADMIN:
      return state.isGlobalAdmin || state.isPlatformAdmin
      
    case PERMISSIONS.PLATFORM_ADMIN:
      return state.isPlatformAdmin || state.isGlobalAdmin
      
    case PERMISSIONS.COMPANY_ACCESS:
      return state.canAccessCompanyManagement || 
             state.hasCompanyAccess || 
             state.isCompanyManagerOrAdmin ||
             state.isGlobalAdmin ||
             state.isPlatformAdmin
      
    case PERMISSIONS.NO_COMPANY_ACCESS:
      // User must NOT have company access (for /company/setup)
      return !state.canAccessCompanyManagement && 
             !state.hasCompanyAccess && 
             !state.isCompanyManagerOrAdmin
      
    case PERMISSIONS.FULL_ACCESS:
      return state.hasFullAccess || state.isGlobalAdmin
      
    case PERMISSIONS.BILLING_ACCESS:
      return state.canAccessBilling || state.isGlobalAdmin
      
    default:
      console.warn(`[GuardEvaluator] Unknown permission: ${permission}`)
      return false
  }
}

/**
 * Evaluate admin route access with security logging
 */
function evaluateAdminRouteAccess(
  path: string,
  state: ReturnType<typeof usePermissionStore.getState>
): GuardResult | null {
  const normalizedPath = normalizeRoutePath(path)

  // If permissions aren't ready, block admin routes (fail closed for security)
  if (!state.permissionsReady) {
    const result: GuardResult = {
      allowed: false,
      loading: true,
      reason: 'Permissions are still loading - admin routes blocked',
      redirectTo: '/dashboard',
      securityLevel: 'admin',
    }
    return result
  }

  const currentUser = Parse.User.current()
  if (
    currentUser &&
    !state.adminCapabilitiesVerified &&
    state.adminCapabilitiesLoading
  ) {
    const result: GuardResult = {
      allowed: false,
      loading: true,
      reason: 'Verifying admin access with server…',
      redirectTo: '/dashboard',
      securityLevel: 'admin',
    }
    return result
  }

  if (currentUser && !state.adminCapabilitiesVerified) {
    const result: GuardResult = {
      allowed: false,
      loading: false,
      reason: 'Admin access not verified',
      redirectTo: '/dashboard',
      securityLevel: 'admin',
    }
    return result
  }

  // Check if user is global or platform admin
  const isAdmin = state.isGlobalAdmin || state.isPlatformAdmin
  if (!isAdmin) {
    // Log security event for unauthorized access attempt
    const u = Parse.User.current()
    logSecurityEvent(SecurityEventType.UNAUTHORIZED_ADMIN_ACCESS, {
      path: normalizedPath,
      userId: u?.id,
      reason: 'User is not a global or platform admin',
    })

    const result: GuardResult = {
      allowed: false,
      reason: 'Global or platform admin access required for admin routes',
      redirectTo: '/dashboard',
      securityLevel: 'admin',
    }
    return result
  }

  // Global or platform admin can access - return null to continue with normal flow
  return null
}

/**
 * Evaluate guard for a specific path
 * Implements defense in depth with multiple security layers
 */
export function evaluateGuard(path: string): GuardResult {
  const state = usePermissionStore.getState()
  const normalizedPath = normalizeRoutePath(path)
  
  // Layer 1: Cache check (performance optimization)
  const cached = guardCache.get(normalizedPath)
  const cacheDuration = getCacheDuration(normalizedPath)
  if (cached && Date.now() - cached.timestamp < cacheDuration) {
    return cached.result
  }
  
  // Layer 2: Admin route security check (highest priority - fail closed)
  if (isAdminRoute(normalizedPath)) {
    const adminResult = evaluateAdminRouteAccess(normalizedPath, state)
    if (adminResult) {
      // Cache the denied result
      guardCache.set(normalizedPath, {
        result: adminResult,
        timestamp: Date.now(),
        path: normalizedPath,
      })
      return adminResult
    }
    // Admin user can proceed - continue to normal flow
  }
  
  // Layer 3: Permission readiness check (for non-admin routes)
  if (!state.permissionsReady) {
    const result: GuardResult = {
      allowed: false,
      loading: true,
      reason: 'Permissions are still loading',
      securityLevel: 'protected',
    }
    guardCache.set(normalizedPath, {
      result,
      timestamp: Date.now(),
      path: normalizedPath,
    })
    return result
  }
  
  // Layer 4: Public route check (fast path for public routes)
  if (isPublicRoute(normalizedPath)) {
    const result: GuardResult = {
      allowed: true,
      securityLevel: 'public',
    }
    guardCache.set(normalizedPath, {
      result,
      timestamp: Date.now(),
      path: normalizedPath,
    })
    return result
  }
  
  // Layer 5: Route metadata validation
  const metadata = getRouteMetadata(normalizedPath)
  
  // Security: Double-check admin routes even if metadata exists (defense in depth)
  if (!metadata) {
    // Fallback: Check if this is an admin route that wasn't caught earlier
    if (isAdminRoute(normalizedPath)) {
      const adminResult = evaluateAdminRouteAccess(normalizedPath, state)
      if (adminResult) {
        guardCache.set(normalizedPath, {
          result: adminResult,
          timestamp: Date.now(),
          path: normalizedPath,
        })
        return adminResult
      }
      // Global admin can access - allow
      const result: GuardResult = {
        allowed: true,
        securityLevel: 'admin',
      }
      guardCache.set(normalizedPath, {
        result,
        timestamp: Date.now(),
        path: normalizedPath,
      })
      return result
    }
    
    // For non-admin routes without metadata, allow by default
    // Route validity is checked separately in routing store
    const result: GuardResult = {
      allowed: true,
      reason: 'No route metadata found',
      securityLevel: 'protected',
    }
    guardCache.set(normalizedPath, {
      result,
      timestamp: Date.now(),
      path: normalizedPath,
    })
    return result
  }
  
  // Layer 6: Permission requirement check
  if (!metadata.requires || metadata.requires.length === 0) {
    // Route has no permission requirements - allow access
    const result: GuardResult = {
      allowed: true,
      securityLevel: metadata.category === 'admin' ? 'admin' : 'protected',
    }
    guardCache.set(normalizedPath, {
      result,
      timestamp: Date.now(),
      path: normalizedPath,
    })
    return result
  }
  
  // Layer 7: Special route handling (e.g., /company/setup)
  if (normalizedPath === '/company/setup') {
    // Global admins can always access (even if they have company access)
    if (state.isGlobalAdmin) {
      const result: GuardResult = {
        allowed: true,
        securityLevel: 'protected',
      }
      guardCache.set(normalizedPath, {
        result,
        timestamp: Date.now(),
        path: normalizedPath,
      })
      return result
    }
    
    // For non-admins, check if they DON'T have company access
    const hasNoCompanyAccess = !state.canAccessCompanyManagement && 
                               !state.hasCompanyAccess && 
                               !state.isCompanyManagerOrAdmin
    
    if (hasNoCompanyAccess) {
      const result: GuardResult = {
        allowed: true,
        securityLevel: 'protected',
      }
      guardCache.set(normalizedPath, {
        result,
        timestamp: Date.now(),
        path: normalizedPath,
      })
      return result
    } else {
      const result: GuardResult = {
        allowed: false,
        reason: 'User already has company access',
        redirectTo: getRedirectTarget(normalizedPath),
        securityLevel: 'protected',
      }
      guardCache.set(normalizedPath, {
        result,
        timestamp: Date.now(),
        path: normalizedPath,
      })
      return result
    }
  }
  
  // Layer 8: Permission evaluation (OR logic - user needs at least one). Entries are legacy
  // flags or company permission keys (SC-439); the keys are answered by GET /api/rbac/me.
  const keyRequirements = metadata.requires.filter((permission) => isPermissionKey(permission))
  const flagRequirements = metadata.requires.filter((permission) => !isPermissionKey(permission))
  let hasRequiredPermission = flagRequirements.some(permission =>
    hasPermission(permission, state)
  )
  if (!hasRequiredPermission && keyRequirements.length > 0) {
    const verdict = resolvePermission(keyRequirements, useRbacStore.getState(), false)
    if (verdict === 'pending') {
      // Not cached: the answer arrives in a moment, and the store's change clears the cache.
      return {
        allowed: false,
        loading: true,
        reason: 'Permissions are still loading',
        securityLevel: 'protected',
      }
    }
    hasRequiredPermission = verdict === true
  }

  if (hasRequiredPermission) {
    const result: GuardResult = {
      allowed: true,
      securityLevel: metadata.category === 'admin' ? 'admin' : 'protected',
    }
    guardCache.set(normalizedPath, {
      result,
      timestamp: Date.now(),
      path: normalizedPath,
    })
    return result
  } else {
    // Log permission check failure
    const currentUser = Parse.User.current()
    logSecurityEvent(SecurityEventType.PERMISSION_CHECK_FAILED, {
      path: normalizedPath,
      userId: currentUser?.id,
      reason: `Missing required permissions: ${metadata.requires.join(', ')}`,
    })

    const result: GuardResult = {
      allowed: false,
      reason: `Missing required permissions: ${metadata.requires.join(', ')}`,
      redirectTo: getRedirectTarget(normalizedPath),
      securityLevel: metadata.category === 'admin' ? 'admin' : 'protected',
    }
    guardCache.set(normalizedPath, {
      result,
      timestamp: Date.now(),
      path: normalizedPath,
    })
    return result
  }
}

/**
 * Invalidate guard cache for a specific path or all paths
 * 
 * @param path - Optional specific path to invalidate. If not provided, clears all cache.
 * @param pattern - Optional pattern to match (e.g., '/admin/*' to clear all admin routes)
 */
export function invalidateGuardCache(path?: string, pattern?: string): void {
  if (path) {
    const normalizedPath = normalizeRoutePath(path)
    guardCache.delete(normalizedPath)
  } else if (pattern) {
    // Invalidate all paths matching the pattern
    const normalizedPattern = normalizeRoutePath(pattern)
    const keysToDelete: string[] = []
    
    guardCache.forEach((entry, key) => {
      if (normalizedPattern.endsWith('/*')) {
        const basePattern = normalizedPattern.slice(0, -2)
        if (key.startsWith(basePattern)) {
          keysToDelete.push(key)
        }
      } else if (key.startsWith(normalizedPattern)) {
        keysToDelete.push(key)
      }
    })
    
    keysToDelete.forEach(key => guardCache.delete(key))
  } else {
    guardCache.clear()
  }
}

/**
 * Get cache statistics (useful for debugging)
 */
export function getCacheStats(): {
  size: number
  adminRoutes: number
  protectedRoutes: number
  publicRoutes: number
} {
  let adminRoutes = 0
  let protectedRoutes = 0
  let publicRoutes = 0

  guardCache.forEach((entry) => {
    const securityLevel = entry.result.securityLevel
    if (securityLevel === 'admin') {
      adminRoutes++
    } else if (securityLevel === 'protected') {
      protectedRoutes++
    } else if (securityLevel === 'public') {
      publicRoutes++
    }
  })

  return {
    size: guardCache.size,
    adminRoutes,
    protectedRoutes,
    publicRoutes,
  }
}

/**
 * Create a guard function for the routing store
 * This function is optimized for routing store usage
 * 
 * Security: Admin routes are always blocked during loading (fail closed)
 */
export function createRouteGuard(): (path: string) => boolean {
  return (path: string): boolean => {
    const normalizedPath = normalizeRoutePath(path)
    const result = evaluateGuard(normalizedPath)
    
    // Security: For admin routes, block during loading (fail closed)
    // For other routes, allow during loading (will be re-evaluated)
    if (result.loading) {
      return !isAdminRoute(normalizedPath)
    }
    
    return result.allowed
  }
}

/**
 * Get guard result with full details (for components)
 */
export function getGuardResult(path: string): GuardResult {
  return evaluateGuard(path)
}

/** Set while the store listeners are attached; the one cleanup that detaches them all. */
let stopGuardCacheInvalidation: (() => void) | null = null

/**
 * Subscribe to permission changes and invalidate cache.
 *
 * Idempotent: initializeRouting() runs from several places (App, RoomSelectionDialog), and each
 * extra listener would clear the guard cache once more on every store change and never be removed.
 * While subscribed, a repeat call returns the same cleanup; after the cleanup runs, a call
 * subscribes again.
 */
export function setupGuardCacheInvalidation(): () => void {
  if (stopGuardCacheInvalidation) return stopGuardCacheInvalidation

  const unsubscribe = usePermissionStore.subscribe(
    (state) => state.permissionsReady,
    (permissionsReady) => {
      // When permissions become ready, invalidate cache
      if (permissionsReady) {
        invalidateGuardCache()
      }
    }
  )
  
  // Also invalidate on any permission change
  const unsubscribe2 = usePermissionStore.subscribe(
    (state) => [
      state.isGlobalAdmin,
      state.isPlatformAdmin,
      state.adminCapabilitiesVerified,
      state.adminCapabilitiesLoading,
      state.canAccessCompanyManagement,
      state.hasCompanyAccess,
      state.isCompanyManagerOrAdmin,
    ],
    () => {
      // Debounce cache invalidation
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }
      
      debounceTimer = setTimeout(() => {
        invalidateGuardCache()
      }, DEBOUNCE_DELAY)
    }
  )
  
  // A new answer from GET /api/rbac/me changes who may open the company pages.
  const unsubscribe3 = useRbacStore.subscribe(
    (state) => state.version,
    () => invalidateGuardCache()
  )

  stopGuardCacheInvalidation = () => {
    unsubscribe()
    unsubscribe2()
    unsubscribe3()
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }
    stopGuardCacheInvalidation = null
  }
  return stopGuardCacheInvalidation
}

